import { useQuery } from "@apollo/client";
import { uniqBy } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import {
  GetUserAssessmentDeliveriesQuery,
  GetUserAssessmentDeliveriesQueryVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import AssessmentDelivery from "@apps/assessments/components/delivery/assessment-delivery";
import getUserAssessmentDeliveriesQuery from "@apps/reporting/graphql/get-user-assessment-deliveries-query";
import useLabel from "@apps/use-label/use-label";
import Loading from "@components/loading/loading";
import { Select } from "@components/select/select";
import { assertEdgesNonNull } from "@helpers/helpers";

const EmployeeDrawerReviews = ({
  target,
  preferredDeliveryProgramId,
  excludedProgramIds,
  onReviewPackageSelected,
}: {
  target: BasicUser;
  preferredDeliveryProgramId?: number;
  excludedProgramIds?: number[];
  onReviewPackageSelected?: (deliveryId: number) => void;
}) => {
  const label = useLabel();
  const [selectedDeliveryId, setSelectedDeliveryId] = useState<
    number | undefined
  >(undefined);

  const { data, loading } = useQuery<
    GetUserAssessmentDeliveriesQuery,
    GetUserAssessmentDeliveriesQueryVariables
  >(getUserAssessmentDeliveriesQuery, {
    variables: { targetId: target.id, excludedProgramIds },
  });

  const deliveryOptions = useMemo(
    () =>
      data
        ? uniqBy(
            [
              ...assertEdgesNonNull(data?.assessmentDeliveries).map(
                (delivery) => ({
                  label: delivery.complianceProgram?.title ?? "Unknown",
                  value: delivery.id,
                  complianceProgramId: delivery.complianceProgram?.id,
                  description: `${moment(
                    delivery.complianceProgram?.startDate,
                  ).format("MMM D, YYYY")} - ${moment(
                    delivery.complianceProgram?.dueDate,
                  ).format("MMM D, YYYY")}`,
                }),
              ),
            ],
            (item) => item.complianceProgramId,
          )
        : [],
    [data],
  );

  useEffect(() => {
    if (!selectedDeliveryId && deliveryOptions.length > 0) {
      const preferredDelivery = deliveryOptions.find(
        (opt) => opt.complianceProgramId === preferredDeliveryProgramId,
      );
      if (preferredDelivery) {
        setSelectedDeliveryId(preferredDelivery.value);
      } else {
        setSelectedDeliveryId(deliveryOptions[0].value);
      }
    }
  }, [deliveryOptions, selectedDeliveryId, preferredDeliveryProgramId]);

  useEffect(() => {
    if (selectedDeliveryId && onReviewPackageSelected) {
      onReviewPackageSelected(selectedDeliveryId);
    }
  }, [selectedDeliveryId, onReviewPackageSelected]);

  return (
    <div>
      {loading ? (
        <div className="p-6">
          <Loading>Loading {label("review", { pluralize: true })}...</Loading>
        </div>
      ) : deliveryOptions.length === 0 ? (
        <div className="p-6 text-gray-500">
          No {label("review", { pluralize: true, capitalize: true })} found
        </div>
      ) : null}
      <div className="empty:hidden">
        {selectedDeliveryId && (
          <AssessmentDelivery
            assessmentDeliveryId={selectedDeliveryId}
            headerAvatarReplacement={
              <div className="flex-1 max-w-[50%]">
                <Select<number>
                  value={selectedDeliveryId}
                  prefix={label("review", { capitalize: true })}
                  options={deliveryOptions}
                  onChange={(option) => setSelectedDeliveryId(option.value)}
                />
              </div>
            }
            isInEmployeeDrawer
          />
        )}
      </div>
    </div>
  );
};

export default EmployeeDrawerReviews;
